import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { AI_MODELS, modelById, modelsByFamily } from '@/lib/aiModels';
import { useAiPrefs, setModel } from '@/store/aiPrefs';
import { cn } from '@/lib/cn';

/**
 * Compact dropdown for picking the AI model. Persists to localStorage so the
 * choice survives reloads.
 */
export function ModelPicker() {
  const prefs = useAiPrefs();
  const current = modelById(prefs.model) || AI_MODELS[0];
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-2.5 h-7 rounded text-2xs font-medium text-fg-secondary hover:bg-overlay hover:text-fg transition-colors"
        title="Choose AI model"
      >
        <span className={dotClass(current.provider)} />
        <span className="font-mono">{current.label}</span>
        <ChevronDown size={11} className="text-fg-muted" />
      </button>
      {open && (
        <div className="absolute bottom-full mb-1 right-0 z-30 w-64 rounded-md border border-border bg-elevated shadow-xl py-1.5">
          {[...modelsByFamily().entries()].map(([family, models]) => (
            <div key={family}>
              <div className="px-3 pt-2 pb-1 text-2xs font-semibold uppercase tracking-wider text-fg-muted">
                {family}
              </div>
              {models.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => {
                    setModel(m.id);
                    setOpen(false);
                  }}
                  className={cn(
                    'w-full px-3 py-1.5 flex items-center gap-2 text-left hover:bg-overlay/60 transition-colors',
                    m.id === current.id ? 'text-fg' : 'text-fg-secondary'
                  )}
                >
                  <span className={dotClass(m.provider)} />
                  <span className="flex-1 text-2xs font-mono">{m.label}</span>
                  {m.hint && <span className="text-2xs text-fg-muted">{m.hint}</span>}
                  {m.id === current.id && <Check size={11} className="text-accent" />}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function dotClass(provider: string): string {
  const base = 'w-1.5 h-1.5 rounded-full shrink-0 ';
  switch (provider) {
    case 'anthropic':
      return base + 'bg-method-put';
    case 'openai':
      return base + 'bg-method-get';
    case 'google':
      return base + 'bg-method-post';
    default:
      return base + 'bg-fg-muted';
  }
}
